// Cognitive Pattern Analyzer - Analyzes learning patterns from test data

/**
 * Analyzes learning style based on test performance patterns
 * @param {Array} testResults - Array of test result objects
 * @returns {string} Learning style: 'visual', 'analytical', 'mixed'
 */
export function analyzeLearningStyle(testResults) {
  if (!testResults || testResults.length === 0) {
    return 'mixed';
  }

  let visualScore = 0;
  let analyticalScore = 0;

  testResults.forEach(test => {
    // Visual learners tend to perform better on diagram/image-based questions
    // Analytical learners perform better on calculation/logic questions
    
    if (test.detailedResults) {
      test.detailedResults.forEach(question => {
        if (question.type === 'diagram' || question.type === 'image') {
          if (question.isCorrect) visualScore += 2;
        } else if (question.type === 'calculation' || question.type === 'logical') {
          if (question.isCorrect) analyticalScore += 2;
        }
      });
    }

    // Time spent patterns: visual learners often spend more time on text-heavy questions
    if (test.sectionalResults) {
      test.sectionalResults.forEach(section => {
        if (section.type === 'reasoning' || section.type === 'english') {
          visualScore += section.accuracy / 10;
        } else if (section.type === 'numerical' || section.type === 'quantitative') {
          analyticalScore += section.accuracy / 10;
        }
      });
    }
  });

  const total = visualScore + analyticalScore;
  if (total === 0) return 'mixed';

  const visualPercentage = (visualScore / total) * 100;
  const analyticalPercentage = (analyticalScore / total) * 100;

  if (Math.abs(visualPercentage - analyticalPercentage) < 15) {
    return 'mixed';
  }

  return visualPercentage > analyticalPercentage ? 'visual' : 'analytical';
}

/**
 * Analyzes time management patterns
 * @param {Array} testResults - Array of test result objects
 * @returns {Object} Time management metrics
 */
export function analyzeTimeManagement(testResults) {
  if (!testResults || testResults.length === 0) {
    return {
      averageTimePerQuestion: 0,
      rushingTendency: 0,
      overthinkingTendency: 0,
      timeEfficiency: 0
    };
  }

  let totalTimePerQuestion = 0;
  let questionCount = 0;
  let rushedQuestions = 0;
  let overthoughtQuestions = 0;

  testResults.forEach(test => {
    if (test.detailedResults) {
      test.detailedResults.forEach(question => {
        const timeSpent = question.timeSpent || 0;
        totalTimePerQuestion += timeSpent;
        questionCount++;

        // Rushed: less than 30 seconds
        if (timeSpent < 30 && !question.isCorrect) {
          rushedQuestions++;
        }

        // Overthinking: more than 5 minutes
        if (timeSpent > 300) {
          overthoughtQuestions++;
        }
      });
    }
  });

  const averageTimePerQuestion = questionCount > 0 ? totalTimePerQuestion / questionCount : 0;
  const rushingTendency = questionCount > 0 ? Math.min(100, (rushedQuestions / questionCount) * 100 * 2) : 0;
  const overthinkingTendency = questionCount > 0 ? Math.min(100, (overthoughtQuestions / questionCount) * 100 * 2) : 0;
  
  // Time efficiency: balance between speed and accuracy
  const timeEfficiency = Math.max(0, 100 - rushingTendency - overthinkingTendency);

  return {
    averageTimePerQuestion: Math.round(averageTimePerQuestion),
    rushingTendency: Math.round(rushingTendency),
    overthinkingTendency: Math.round(overthinkingTendency),
    timeEfficiency: Math.round(timeEfficiency)
  };
}

/**
 * Analyzes problem-solving approach
 * @param {Array} detailedResults - Detailed question results from tests
 * @returns {Object} Problem-solving approach metrics
 */
export function analyzeQuestionApproach(testResults) {
  if (!testResults || testResults.length === 0) {
    return {
      methodical: 50,
      intuitive: 50,
      skipAndReturn: 0
    };
  }

  let methodicalScore = 0;
  let intuitiveScore = 0;
  let skipCount = 0;
  let totalQuestions = 0;

  testResults.forEach(test => {
    if (test.detailedResults) {
      test.detailedResults.forEach((question, index) => {
        totalQuestions++;
        const timeSpent = question.timeSpent || 0;

        // Methodical: consistent time spent, step-by-step approach
        if (timeSpent > 60 && timeSpent < 240) {
          methodicalScore++;
        }

        // Intuitive: quick decisions, either very fast correct or fast incorrect
        if (timeSpent < 60) {
          intuitiveScore++;
        }

        // Skip and return: unattempted questions or very long time gaps
        if (question.status === 'unattempted' || question.status === 'skipped') {
          skipCount++;
        }
      });
    }
  });

  const total = methodicalScore + intuitiveScore;
  const methodicalPercentage = total > 0 ? (methodicalScore / total) * 100 : 50;
  const intuitivePercentage = total > 0 ? (intuitiveScore / total) * 100 : 50;
  const skipPercentage = totalQuestions > 0 ? (skipCount / totalQuestions) * 100 : 0;

  return {
    methodical: Math.round(methodicalPercentage),
    intuitive: Math.round(intuitivePercentage),
    skipAndReturn: Math.round(skipPercentage)
  };
}

/**
 * Identifies strengths and weaknesses by topic
 * @param {Array} testResults - Array of test result objects
 * @returns {Object} Strengths and weaknesses
 */
export function identifyStrengthsAndWeaknesses(testResults) {
  if (!testResults || testResults.length === 0) {
    return {
      strengths: [],
      weaknesses: []
    };
  }

  const topicPerformance = {};

  testResults.forEach(test => {
    if (test.detailedResults) {
      test.detailedResults.forEach(question => {
        const topic = question.topic || 'General';
        
        if (!topicPerformance[topic]) {
          topicPerformance[topic] = {
            topic,
            correct: 0,
            total: 0,
            totalTime: 0
          };
        }

        topicPerformance[topic].total++;
        if (question.isCorrect) {
          topicPerformance[topic].correct++;
        }
        topicPerformance[topic].totalTime += question.timeSpent || 0;
      });
    }
  });

  // Calculate scores and confidence
  const topics = Object.values(topicPerformance).map(topic => ({
    topic: topic.topic,
    score: topic.total > 0 ? Math.round((topic.correct / topic.total) * 100) : 0,
    confidence: Math.min(100, topic.total * 5), // More attempts = higher confidence
    questionsAttempted: topic.total,
    averageTime: topic.total > 0 ? Math.round(topic.totalTime / topic.total) : 0
  }));

  // Sort by score
  topics.sort((a, b) => b.score - a.score);

  // Top 5 strengths (score >= 70)
  const strengths = topics.filter(t => t.score >= 70).slice(0, 5);

  // Top 5 weaknesses (score < 60)
  const weaknesses = topics.filter(t => t.score < 60).slice(0, 5);

  return {
    strengths,
    weaknesses
  };
}

/**
 * Calculates knowledge retention metrics
 * @param {Array} testResults - Array of test result objects
 * @param {Object} revisionData - Revision activity data
 * @returns {Object} Knowledge retention metrics
 */
export function calculateKnowledgeRetention(testResults, revisionData) {
  if (!testResults || testResults.length < 2) {
    return {
      shortTerm: 0,
      midTerm: 0,
      longTerm: 0,
      improvementRate: 0
    };
  }

  // Filter tests with valid dates and scores
  const validTests = testResults.filter(test => {
    const hasDate = test.completedAt || test.createdAt || test.submittedAt || test.timestamp;
    const hasScore = typeof test.accuracy === 'number' || typeof test.score === 'number' || typeof test.percentage === 'number';
    return hasDate && hasScore;
  });

  if (validTests.length < 2) {
    return {
      shortTerm: 0,
      midTerm: 0,
      longTerm: 0,
      improvementRate: 0
    };
  }

  // Sort tests by date
  const sortedTests = [...validTests].sort((a, b) => {
    const dateA = new Date(a.completedAt || a.createdAt || a.submittedAt || a.timestamp);
    const dateB = new Date(b.completedAt || b.createdAt || b.submittedAt || b.timestamp);
    return dateA - dateB;
  });

  // Helper to get score from test
  const getScore = (test) => {
    return test.accuracy || test.score || test.percentage || 0;
  };

  // Short-term retention: performance within 7 days
  let shortTermScore = 0;
  let shortTermCount = 0;

  // Mid-term retention: performance between 8-29 days
  let midTermScore = 0;
  let midTermCount = 0;

  // Long-term retention: performance after 30+ days
  let longTermScore = 0;
  let longTermCount = 0;

  for (let i = 1; i < sortedTests.length; i++) {
    const currentTest = sortedTests[i];
    const previousTest = sortedTests[i - 1];
    
    const currentDate = new Date(currentTest.completedAt || currentTest.createdAt || currentTest.submittedAt || currentTest.timestamp);
    const previousDate = new Date(previousTest.completedAt || previousTest.createdAt || previousTest.submittedAt || previousTest.timestamp);
    
    const daysDiff = Math.floor((currentDate - previousDate) / (1000 * 60 * 60 * 24));

    const currentScore = getScore(currentTest);
    const previousScore = getScore(previousTest);

    if (daysDiff <= 7 && daysDiff >= 0) {
      // Short-term comparison
      if (currentScore >= previousScore) {
        shortTermScore += currentScore;
      } else {
        shortTermScore += currentScore * 0.8; // Penalty for decline
      }
      shortTermCount++;
    } else if (daysDiff >= 8 && daysDiff <= 29) {
      // Mid-term comparison
      if (currentScore >= previousScore) {
        midTermScore += currentScore;
      } else {
        midTermScore += currentScore * 0.75; // Medium penalty
      }
      midTermCount++;
    } else if (daysDiff >= 30) {
      // Long-term comparison
      if (currentScore >= previousScore) {
        longTermScore += currentScore;
      } else {
        longTermScore += currentScore * 0.7; // Larger penalty for long-term decline
      }
      longTermCount++;
    }
  }

  const shortTerm = shortTermCount > 0 ? Math.round(shortTermScore / shortTermCount) : 0;
  const midTerm = midTermCount > 0 ? Math.round(midTermScore / midTermCount) : 0;
  const longTerm = longTermCount > 0 ? Math.round(longTermScore / longTermCount) : 0;

  // Calculate improvement rate
  const firstScore = getScore(sortedTests[0]);
  const lastScore = getScore(sortedTests[sortedTests.length - 1]);
  const improvementRate = firstScore > 0 ? Math.round(((lastScore - firstScore) / firstScore) * 100) : 0;

  return {
    shortTerm: isNaN(shortTerm) ? 0 : Math.min(100, Math.max(0, shortTerm)),
    midTerm: isNaN(midTerm) ? 0 : Math.min(100, Math.max(0, midTerm)),
    longTerm: isNaN(longTerm) ? 0 : Math.min(100, Math.max(0, longTerm)),
    improvementRate: isNaN(improvementRate) ? 0 : improvementRate
  };
}

/**
 * Analyzes profile data alignment with actual performance
 * @param {Object} profileSettings - User's profile settings from Settings page
 * @param {Object} analyzedData - Analyzed cognitive and behavioral data
 * @returns {Object} Profile alignment analysis
 */
export function analyzeProfileAlignment(profileSettings, analyzedData) {
  if (!profileSettings || !profileSettings.profile) {
    console.log('No profile settings found:', profileSettings);
    return {
      hasProfileData: false,
      alignments: [],
      discrepancies: []
    };
  }

  const { profile } = profileSettings;
  console.log('Profile data received:', {
    hasPersonalInfo: !!profile.personalInfo,
    hasContactInfo: !!profile.contactInfo,
    hasParentsInfo: !!profile.parentsInfo,
    hasResults: !!profile.results,
    hasDocuments: !!profile.documents,
    hasLearningPreferences: !!profile.learningPreferences,
    profileKeys: Object.keys(profile)
  });
  const alignments = [];
  const discrepancies = [];

  // Learning style alignment
  if (profile.learningPreferences && profile.learningPreferences.length > 0) {
    const stated = profile.learningPreferences[0].toLowerCase();
    const analyzed = analyzedData.learningStyle;
    
    if (stated === analyzed || (stated.includes(analyzed) || analyzed.includes(stated))) {
      alignments.push({
        aspect: 'Learning Style',
        stated,
        analyzed,
        confidence: 'high',
        message: 'Your self-reported learning style matches your actual performance patterns'
      });
    } else {
      discrepancies.push({
        aspect: 'Learning Style',
        stated,
        analyzed,
        severity: 'medium',
        message: `You identified as a ${stated} learner, but your performance suggests ${analyzed} learning patterns`,
        recommendation: `Try incorporating more ${analyzed} learning techniques to improve performance`
      });
    }
  }

  // Study time preferences vs actual behavior
  if (profile.preferredStudyTime && analyzedData.studySchedule) {
    const stated = profile.preferredStudyTime;
    const actual = analyzedData.studySchedule.preferredTime;
    
    if (stated === actual) {
      alignments.push({
        aspect: 'Study Time',
        stated,
        analyzed: actual,
        confidence: 'high',
        message: 'You study during your preferred time slots'
      });
    } else {
      discrepancies.push({
        aspect: 'Study Time',
        stated,
        analyzed: actual,
        severity: 'low',
        message: `You prefer studying in the ${stated}, but actually study more in the ${actual}`,
        recommendation: 'Consider adjusting your schedule to match your preferred study time for better efficiency'
      });
    }
  }

  // Goals tracking
  const goalsProgress = [];
  if (profile.goals && profile.goals.length > 0) {
    profile.goals.forEach(goal => {
      goalsProgress.push({
        goal: goal.goal || goal,
        targetDate: goal.targetDate,
        progress: calculateGoalProgress(goal, analyzedData),
        onTrack: (goal.progress || 0) >= 50
      });
    });
  }

  return {
    hasProfileData: true,
    alignments,
    discrepancies,
    goalsProgress,
    profileCompleteness: calculateProfileCompleteness(profile)
  };
}

/**
 * Calculates goal progress based on actual performance
 */
function calculateGoalProgress(goal, analyzedData) {
  // Simple progress calculation - can be enhanced based on goal type
  if (goal.type === 'test_score' && analyzedData.testPerformance) {
    const currentAvg = analyzedData.testPerformance.averageScore || 0;
    const target = goal.target || 100;
    return Math.min(100, Math.round((currentAvg / target) * 100));
  }
  
  return goal.progress || 0;
}

/**
 * Calculates profile completeness percentage
 */
function calculateProfileCompleteness(profile) {
  if (!profile) return 0;

  // Check for analysis-specific fields (learning preferences)
  const analysisFields = [
    'learningPreferences',
    'studyHabits',
    'preferredStudyTime',
    'goals',
    'strengths',
    'weaknesses',
    'interests'
  ];

  // Check for student profile fields (from Settings page)
  const studentProfileFields = [
    'personalInfo',
    'contactInfo',
    'parentsInfo',
    'results',
    'documents'
  ];

  let analysisCompleted = 0;
  let studentProfileCompleted = 0;

  // Count analysis fields
  analysisFields.forEach(field => {
    if (profile[field] && (Array.isArray(profile[field]) ? profile[field].length > 0 : profile[field])) {
      analysisCompleted++;
    }
  });

  // Count student profile fields with nested checks
  if (profile.personalInfo) {
    const personalFields = ['firstName', 'lastName', 'email', 'phone', 'dateOfBirth', 'gender'];
    const filledPersonal = personalFields.filter(f => profile.personalInfo[f]).length;
    if (filledPersonal >= 4) studentProfileCompleted++; // At least 4 out of 6 fields
  }

  if (profile.contactInfo) {
    const contactFields = ['email', 'phone', 'address', 'city', 'state'];
    const filledContact = contactFields.filter(f => profile.contactInfo[f]).length;
    if (filledContact >= 3) studentProfileCompleted++; // At least 3 out of 5 fields
  }

  if (profile.parentsInfo) {
    const parentFields = ['fatherName', 'fatherPhone', 'motherName', 'motherPhone'];
    const filledParent = parentFields.filter(f => profile.parentsInfo[f]).length;
    if (filledParent >= 2) studentProfileCompleted++; // At least 2 out of 4 fields
  }

  if (profile.results) {
    const hasClass10 = profile.results.class10?.percentage || profile.results.class10?.board;
    const hasClass12 = profile.results.class12?.percentage || profile.results.class12?.board;
    if (hasClass10 || hasClass12) studentProfileCompleted++;
  }

  if (profile.documents) {
    const hasDocuments = profile.documents.photo || profile.documents.signature || 
                        (profile.documents.otherDocuments && profile.documents.otherDocuments.length > 0);
    if (hasDocuments) studentProfileCompleted++;
  }

  // Calculate weighted completeness
  // 40% weight for analysis fields, 60% weight for student profile
  const analysisPercentage = (analysisCompleted / analysisFields.length) * 40;
  const studentProfilePercentage = (studentProfileCompleted / studentProfileFields.length) * 60;
  
  return Math.round(analysisPercentage + studentProfilePercentage);
}

/**
 * Generates comprehensive cognitive profile with user profile integration
 * @param {Array} testResults - Array of test result objects
 * @param {Object} revisionData - Revision activity data
 * @param {Object} profileSettings - User's profile settings (optional)
 * @returns {Object} Complete cognitive profile
 */
export function generateCognitiveProfile(testResults, revisionData, profileSettings = null) {
  const learningStyle = analyzeLearningStyle(testResults);
  const timeManagement = analyzeTimeManagement(testResults);
  const problemSolvingApproach = analyzeQuestionApproach(testResults);
  const { strengths, weaknesses } = identifyStrengthsAndWeaknesses(testResults);
  const knowledgeRetention = calculateKnowledgeRetention(testResults, revisionData);

  // Integrate profile data if available
  const profileAlignment = profileSettings 
    ? analyzeProfileAlignment(profileSettings, { 
        learningStyle, 
        timeManagement,
        testPerformance: { averageScore: calculateAverageScore(testResults) }
      })
    : null;

  return {
    learningStyle,
    strengths,
    weaknesses,
    timeManagement,
    problemSolvingApproach,
    knowledgeRetention,
    profileAlignment
  };
}

/**
 * Helper to calculate average test score
 */
function calculateAverageScore(testResults) {
  if (!testResults || testResults.length === 0) return 0;
  const total = testResults.reduce((sum, test) => sum + (test.accuracy || test.score || 0), 0);
  return Math.round(total / testResults.length);
}
